<template>
    <div
        class="modern-shell"
        :class="{
            'modern-shell--immersive': hideNavigation,
            'modern-shell--telegram': isTelegramWebApp,
            'modern-shell--bottom-nav-hidden': hideMobileBottomNavigation,
            'modern-shell--sidebar-collapsed': effectiveDesktopSidebarCollapsed,
            'modern-shell--sidebar-compact': compactSidebar,
            'modern-shell--sidebar-flyout-open': sidebarFlyoutOpen
        }"
    >
        <template v-if="!hideDesktopNavigation">
            <header class="modern-shell__header">
                <router-link to="/" class="modern-shell__brand" aria-label="Аргус — на главную">
                    <img src="/icons/icon-192.png" alt="" class="modern-shell__brand-icon">
                    <span class="modern-shell__brand-copy">
                        <strong>Аргус</strong>
                        <small>{{ companyName }}</small>
                    </span>
                </router-link>

                <div class="modern-shell__page-title">{{ pageTitle }}</div>

                <div
                    v-if="installActionResolved"
                    ref="accountMenu"
                    class="modern-shell__header-actions"
                >
                    <button
                        v-if="showInstallAction"
                        type="button"
                        class="modern-shell__install-action"
                        title="Установить приложение Аргус"
                        aria-label="Установить приложение Аргус"
                        :disabled="installActionBusy"
                        @click="openInstallPage"
                    >
                        <i class="fas fa-download" aria-hidden="true"></i>
                        <span class="modern-shell__install-label modern-shell__install-label--desktop">
                            Установить приложение Аргус
                        </span>
                        <span class="modern-shell__install-label modern-shell__install-label--mobile">
                            Установить Аргус
                        </span>
                    </button>

                    <button
                        type="button"
                        class="modern-shell__profile"
                        :aria-label="userShortName + '. Профиль'"
                        aria-haspopup="menu"
                        :aria-expanded="String(accountMenuOpen)"
                        @click="toggleAccountMenu"
                    >
                        <span class="modern-shell__profile-copy">
                            <strong>{{ userShortName }}</strong>
                            <small>Профиль</small>
                        </span>
                        <span class="modern-shell__avatar" aria-hidden="true">
                            <img
                                v-if="userAvatarUrl"
                                :src="userAvatarUrl"
                                alt=""
                                @error="handleUserAvatarError"
                            >
                            <template v-else>{{ userInitials }}</template>
                        </span>
                        <i
                            class="fas fa-chevron-down modern-shell__profile-chevron"
                            :class="{'modern-shell__profile-chevron--open': accountMenuOpen}"
                            aria-hidden="true"
                        ></i>
                    </button>

                    <div
                        v-if="accountMenuOpen"
                        class="modern-shell__account-menu"
                        role="menu"
                    >
                        <div class="modern-shell__account-summary">
                            <strong>{{ userShortName }}</strong>
                            <small>{{ companyName }}</small>
                        </div>
                        <router-link
                            to="/profile"
                            class="modern-shell__account-item"
                            role="menuitem"
                            @click.native="closeAccountMenu"
                        >
                            <i class="fas fa-user-circle" aria-hidden="true"></i>
                            <span>Мой профиль</span>
                        </router-link>
                        <router-link
                            to="/about"
                            class="modern-shell__account-item"
                            role="menuitem"
                            @click.native="closeAccountMenu"
                        >
                            <i class="fas fa-info-circle" aria-hidden="true"></i>
                            <span>О приложении</span>
                        </router-link>
                        <button
                            type="button"
                            class="modern-shell__account-item modern-shell__account-item--logout"
                            role="menuitem"
                            @click="logoutFromAccountMenu"
                        >
                            <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
                            <span>Выйти</span>
                        </button>
                    </div>
                </div>
            </header>

            <button
                v-if="sidebarFlyoutOpen"
                type="button"
                class="modern-shell__sidebar-backdrop"
                aria-label="Закрыть меню"
                @click="closeSidebarFlyout(true)"
            ></button>

            <aside
                class="modern-shell__sidebar"
                aria-label="Основная навигация"
            >
                <nav class="modern-shell__sidebar-scroll">
                    <div class="modern-shell__nav-group">
                        <router-link
                            v-for="link in desktopPrimaryLinks"
                            :key="link.key"
                            :to="link.to"
                            class="modern-shell__nav-link"
                            :class="{'modern-shell__nav-link--active': isPrimaryActive(link.key)}"
                            :title="link.label"
                            @click.native="closeSidebarFlyout()"
                        >
                            <span class="modern-shell__nav-icon">
                                <i :class="link.icon" aria-hidden="true"></i>
                            </span>
                            <span class="modern-shell__nav-label">{{ link.label }}</span>
                            <span
                                v-if="link.badge"
                                class="modern-shell__nav-badge"
                                :aria-label="`Требуют внимания: ${link.badge}`"
                            >
                                {{ formatBadge(link.badge) }}
                            </span>
                        </router-link>
                    </div>

                    <div
                        v-if="navigationSections.length"
                        class="modern-shell__nav-group modern-shell__catalog"
                    >
                        <section
                            v-for="section in navigationSections"
                            :key="section.key"
                            class="modern-shell__nav-section"
                        >
                            <button
                                v-if="compactSidebar"
                                type="button"
                                class="modern-shell__nav-toggle"
                                :class="{
                                    'modern-shell__nav-toggle--active': nodeContainsActive(section),
                                    'modern-shell__nav-toggle--selected': isFlyoutSection(section)
                                }"
                                :aria-expanded="String(isFlyoutSection(section))"
                                :data-navigation-section-key="section.key"
                                :title="section.label"
                                @click="toggleSidebarFlyout(section)"
                            >
                                <span class="modern-shell__nav-icon">
                                    <i :class="section.icon" aria-hidden="true"></i>
                                </span>
                                <span class="modern-shell__nav-label">{{ section.label }}</span>
                            </button>

                            <button
                                v-else
                                type="button"
                                class="modern-shell__nav-toggle"
                                :class="{'modern-shell__nav-toggle--active': nodeContainsActive(section)}"
                                :aria-expanded="String(isNavigationGroupOpen(section))"
                                @click="toggleNavigationGroup(section)"
                            >
                                <span class="modern-shell__nav-icon">
                                    <i :class="section.icon" aria-hidden="true"></i>
                                </span>
                                <span class="modern-shell__nav-label">{{ section.label }}</span>
                                <i
                                    class="fas modern-shell__nav-toggle-chevron"
                                    :class="isNavigationGroupOpen(section) ? 'fa-chevron-up' : 'fa-chevron-down'"
                                    aria-hidden="true"
                                ></i>
                            </button>

                            <div
                                v-show="shouldShowNavigationSection(section)"
                                class="modern-shell__nav-children"
                            >
                                <template v-for="item in section.items">
                                    <div
                                        v-if="item.kind === 'group'"
                                        :key="item.key"
                                        class="modern-shell__nav-subgroup"
                                    >
                                        <button
                                            type="button"
                                            class="modern-shell__nav-subtoggle"
                                            :class="{'modern-shell__nav-subtoggle--active': nodeContainsActive(item)}"
                                            :aria-expanded="String(isNavigationGroupOpen(item))"
                                            @click="toggleNavigationGroup(item)"
                                        >
                                            <i :class="item.icon" aria-hidden="true"></i>
                                            <span>{{ item.label }}</span>
                                            <i
                                                class="fas modern-shell__nav-toggle-chevron"
                                                :class="isNavigationGroupOpen(item) ? 'fa-chevron-up' : 'fa-chevron-down'"
                                                aria-hidden="true"
                                            ></i>
                                        </button>
                                        <div
                                            v-show="isNavigationGroupOpen(item)"
                                            class="modern-shell__nav-grandchildren"
                                        >
                                            <router-link
                                                v-for="link in item.items"
                                                :key="link.key"
                                                :to="link.path"
                                                class="modern-shell__nav-link modern-shell__nav-link--nested"
                                                :class="{'modern-shell__nav-link--active': isNavigationLinkActive(link)}"
                                                :title="link.label"
                                                @click.native="closeSidebarFlyout()"
                                            >
                                                <span class="modern-shell__nav-icon">
                                                    <i :class="link.icon" aria-hidden="true"></i>
                                                </span>
                                                <span class="modern-shell__nav-label">{{ link.label }}</span>
                                            </router-link>
                                        </div>
                                    </div>

                                    <router-link
                                        v-else
                                        :key="item.key"
                                        :to="item.path"
                                        class="modern-shell__nav-link modern-shell__nav-link--section"
                                        :class="{'modern-shell__nav-link--active': isNavigationLinkActive(item)}"
                                        :title="item.label"
                                        @click.native="closeSidebarFlyout()"
                                    >
                                        <span class="modern-shell__nav-icon">
                                            <i :class="item.icon" aria-hidden="true"></i>
                                        </span>
                                        <span class="modern-shell__nav-label">{{ item.label }}</span>
                                    </router-link>
                                </template>
                            </div>
                        </section>
                    </div>
                </nav>
                <button
                    v-if="canPinDesktopSidebar"
                    type="button"
                    class="modern-shell__sidebar-collapse"
                    :title="effectiveDesktopSidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'"
                    :aria-label="effectiveDesktopSidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'"
                    :aria-expanded="String(!effectiveDesktopSidebarCollapsed)"
                    @click="toggleDesktopSidebar"
                >
                    <i
                        class="fas"
                        :class="effectiveDesktopSidebarCollapsed ? 'fa-bars' : 'fa-arrow-left'"
                        aria-hidden="true"
                    ></i>
                    <span>{{ effectiveDesktopSidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню' }}</span>
                </button>
            </aside>
        </template>

        <div class="modern-shell__content">
            <slot></slot>
        </div>

        <nav
            v-if="!hideNavigation && !hideMobileBottomNavigation"
            class="modern-shell__bottom-nav"
            :class="{'modern-shell__bottom-nav--telegram': isTelegramWebApp}"
            aria-label="Основная навигация"
        >
            <router-link
                v-for="link in bottomNavigationLinks"
                :key="link.key"
                :to="link.to"
                class="modern-shell__bottom-link"
                :class="{'modern-shell__bottom-link--active': isPrimaryActive(link.key)}"
                :aria-label="link.label"
            >
                <span class="modern-shell__bottom-icon">
                    <i :class="link.icon" aria-hidden="true"></i>
                    <span
                        v-if="link.badge"
                        class="modern-shell__bottom-badge"
                        :aria-label="`Требуют внимания: ${link.badge}`"
                    >
                        {{ formatBadge(link.badge) }}
                    </span>
                </span>
                <span>{{ link.shortLabel || link.label }}</span>
            </router-link>
        </nav>
    </div>
</template>

<script>
import {
    getAvailableWorkspaces,
    getModernNavigationSections,
    getWorkspaceByKey
} from '@/utils/workspaceRegistry';
import {rememberRecentRoute} from '@/utils/recentWork';
import {
    DESKTOP_SIDEBAR_COLLAPSE_REQUEST_EVENT,
    getDesktopSidebarCollapsed,
    getOpenNavigationGroups,
    saveDesktopSidebarCollapsed,
    saveOpenNavigationGroups
} from '@/utils/modernNavigationState';
import {
    hasTelegramInstallSuccessEligible,
    INSTALLATION_STATE_CHANGED_EVENT,
    isIOSInstallDevice,
    markInstallPromotionSessionDismissed,
    openInstallUrlExternally,
    prepareTelegramInstallHandoff,
    shouldShowInstallPromotion
} from '@/utils/appInstallation';

const PAGE_TITLES = {
    '/': 'Главная',
    '/tasks': 'Задачи',
    '/requests': 'Заявки',
    '/more': 'Ещё',
    '/profile': 'Мой профиль',
    '/about': 'О приложении',
    '/objects': 'Объекты',
    '/reports': 'Отчёты',
    '/timesheets': 'Табели',
    '/payroll': 'Начисление зарплаты',
    '/mechanic-assets': 'Техника',
    '/fuel-cards': 'Топливные карты',
    '/fuel-limits': 'Лимиты по топливу',
    '/gallery': 'Галерея',
    '/employees': 'Сотрудники',
    '/users': 'Учётные записи'
};

function routePath(location) {
    if (!location) return '';
    if (typeof location === 'string') return location.split('?')[0];
    return location.path || '';
}

function routeQuery(location) {
    if (!location || typeof location !== 'string' || location.indexOf('?') === -1) {
        return {};
    }
    return location
        .split('?')[1]
        .split('&')
        .reduce((result, part) => {
            const pair = part.split('=');
            result[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
            return result;
        }, {});
}

export default {
    name: 'ModernShell',
    data() {
        return {
            attentionTimer: null,
            fallbackNoticeShown: false,
            accessContextLoaded: false,
            openNavigationGroups: [],
            closedNavigationGroups: [],
            accountMenuOpen: false,
            showInstallAction: false,
            installActionResolved: false,
            installationCheckId: 0,
            installActionBusy: false,
            userAvatarUrl: null,
            desktopSidebarCollapsed: false,
            temporaryDesktopSidebarCollapsed: null,
            temporaryDesktopSidebarRouteKey: null,
            viewportWidth: typeof window === 'undefined' ? 1200 : window.innerWidth,
            flyoutNavigationSectionKey: null
        };
    },
    computed: {
        user() {
            return this.$store.getters.currentUser || {};
        },
        navigation() {
            return this.$store.state.navigationExperience || {};
        },
        companyName() {
            return this.user.companyName || '';
        },
        userShortName() {
            const parts = [this.user.lastName, this.user.firstName].filter(Boolean);
            return parts.join(' ') || 'Профиль';
        },
        userInitials() {
            const initials = [this.user.firstName, this.user.lastName]
                .filter(Boolean)
                .map(value => String(value).charAt(0).toUpperCase())
                .join('');
            return initials || 'А';
        },
        pageTitle() {
            const routeTitle = this.$route.meta && this.$route.meta.title;
            if (routeTitle) return routeTitle;
            const exact = PAGE_TITLES[this.$route.path];
            if (exact) return exact;
            const prefix = Object.keys(PAGE_TITLES)
                .filter(path => path !== '/' && this.$route.path.indexOf(`${path}/`) === 0)
                .sort((left, right) => right.length - left.length)[0];
            return prefix ? PAGE_TITLES[prefix] : 'Аргус';
        },
        isTelegramWebApp() {
            return this.$store.getters.isTelegramWebApps;
        },
        hideNavigation() {
            const isTaskCreation = this.$route.name === 'task-card' &&
                String(this.$route.params.id || '').toLowerCase() === 'new';
            return isTaskCreation ||
                this.$route.matched.some(({meta}) =>
                    meta.standalone === true ||
                    meta.hideAppNavigation === true ||
                    meta.immersive === true
                );
        },
        hideDesktopNavigation() {
            return this.isTelegramWebApp || this.hideNavigation;
        },
        hideMobileBottomNavigation() {
            return this.$route.matched.some(({meta}) => (
                meta.hideMobileBottomNavigation === true
            ));
        },
        taskAttention() {
            return Number(
                this.navigation.attention &&
                this.navigation.attention.tasks &&
                this.navigation.attention.tasks.total
            ) || 0;
        },
        requestAttention() {
            return Number(
                this.navigation.attention &&
                this.navigation.attention.requests
            ) || 0;
        },
        availableWorkspaces() {
            return getAvailableWorkspaces(this);
        },
        effectiveWorkspace() {
            const availableKeys = new Set(this.availableWorkspaces.map(item => item.key));
            const preferred = this.navigation.preferred_workspace_key;
            if (preferred && availableKeys.has(preferred)) {
                return getWorkspaceByKey(preferred);
            }

            const departmentDefaults = Array.isArray(this.navigation.department_default_workspace_keys)
                ? this.navigation.department_default_workspace_keys
                : [];
            const availableDefaults = departmentDefaults.filter(key => availableKeys.has(key));
            if (availableDefaults.length === 1) {
                return getWorkspaceByKey(availableDefaults[0]);
            }
            if (availableDefaults.length > 1) {
                return null;
            }
            return this.availableWorkspaces[0] || null;
        },
        primaryLinks() {
            return [
                {
                    key: 'home',
                    label: 'Главная',
                    shortLabel: 'Главная',
                    to: '/',
                    icon: 'fas fa-home'
                },
                {
                    key: 'tasks',
                    label: 'Задачи',
                    shortLabel: 'Задачи',
                    to: '/tasks',
                    icon: 'fas fa-check-circle',
                    badge: this.taskAttention
                },
                {
                    key: 'requests',
                    label: 'Заявки',
                    shortLabel: 'Заявки',
                    to: '/requests',
                    icon: 'fas fa-clipboard-list',
                    badge: this.requestAttention
                }
            ];
        },
        mobileLinks() {
            const workspace = this.effectiveWorkspace;
            return this.primaryLinks.concat([
                {
                    key: 'workspace',
                    label: workspace ? workspace.label : 'Мой раздел',
                    shortLabel: workspace ? workspace.shortLabel : 'Раздел',
                    to: workspace ? workspace.path : '/profile',
                    icon: workspace ? workspace.icon : 'fas fa-star'
                },
                this.moreLink
            ]);
        },
        moreLink() {
            return {
                key: 'more',
                label: 'Ещё',
                shortLabel: 'Ещё',
                to: '/more',
                icon: 'fas fa-th-large'
            };
        },
        bottomNavigationLinks() {
            if (this.isTelegramWebApp) {
                return this.primaryLinks.concat(this.moreLink);
            }
            return this.mobileLinks;
        },
        desktopPrimaryLinks() {
            return this.mobileLinks.filter(link => link.key !== 'more');
        },
        navigationSections() {
            return getModernNavigationSections(this);
        },
        compactSidebar() {
            if (this.viewportWidth < 768) return false;
            if (this.viewportWidth < 1200) return true;
            return this.effectiveDesktopSidebarCollapsed;
        },
        effectiveDesktopSidebarCollapsed() {
            return this.temporaryDesktopSidebarRouteKey
                ? this.temporaryDesktopSidebarCollapsed === true
                : this.desktopSidebarCollapsed;
        },
        canPinDesktopSidebar() {
            return this.viewportWidth >= 1200;
        },
        flyoutNavigationSection() {
            return this.navigationSections.find(section => (
                section.key === this.flyoutNavigationSectionKey
            )) || null;
        },
        sidebarFlyoutOpen() {
            return Boolean(
                this.compactSidebar
                && this.flyoutNavigationSection
                && !this.hideDesktopNavigation
            );
        },
        isMoreActive() {
            return this.isRouteUnder('/more') ||
                this.isRouteUnder('/profile') ||
                this.isRouteUnder('/about');
        }
    },
    watch: {
        '$route.fullPath': {
            immediate: true,
            handler() {
                this.syncTemporaryDesktopSidebarForRoute();
                this.closeAccountMenu();
                this.closeSidebarFlyout();
                this.closedNavigationGroups = [];
                this.syncActiveNavigationSection();
                rememberRecentRoute(this.$route, this.user);
                if (!this.hideNavigation) {
                    this.refreshAttention();
                }
            }
        },
        effectiveWorkspace() {
            this.showFallbackNoticeIfNeeded();
        },
        navigationSections() {
            this.syncActiveNavigationSection();
        },
        'user.userId'() {
            this.restoreOpenNavigationGroups();
            this.restoreDesktopSidebar();
            this.loadUserAvatar();
        }
    },
    mounted() {
        this.restoreOpenNavigationGroups();
        this.restoreDesktopSidebar();
        this.loadUserAvatar();
        this.loadAccessContext();
        this.refreshAttention();
        this.refreshInstallAction();
        this.attentionTimer = window.setInterval(this.refreshAttention, 60000);
        window.addEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.refreshInstallAction
        );
        window.addEventListener(
            DESKTOP_SIDEBAR_COLLAPSE_REQUEST_EVENT,
            this.handleDesktopSidebarCollapseRequest
        );
        document.addEventListener('mousedown', this.handleAccountMenuOutside);
        document.addEventListener('keydown', this.handleAccountMenuKeydown);
        window.addEventListener('resize', this.handleViewportResize);
    },
    beforeDestroy() {
        if (this.attentionTimer) {
            window.clearInterval(this.attentionTimer);
            this.attentionTimer = null;
        }
        window.removeEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.refreshInstallAction
        );
        window.removeEventListener(
            DESKTOP_SIDEBAR_COLLAPSE_REQUEST_EVENT,
            this.handleDesktopSidebarCollapseRequest
        );
        document.removeEventListener('mousedown', this.handleAccountMenuOutside);
        document.removeEventListener('keydown', this.handleAccountMenuKeydown);
        window.removeEventListener('resize', this.handleViewportResize);
    },
    methods: {
        async loadUserAvatar() {
            const userId = Number(this.user.userId);
            this.userAvatarUrl = null;
            if (!userId) return;
            try {
                const profile = await this.$store.dispatch('getMyProfile');
                const profileUser = profile && profile.user ? profile.user : {};
                if (
                    Number(profileUser.user_id) !== userId ||
                    (!profileUser.has_avatar && !profileUser.avatar_url)
                ) return;
                const stampRaw = profileUser.avatar_updated_at
                    ? new Date(profileUser.avatar_updated_at).getTime()
                    : null;
                const stamp = Number.isFinite(stampRaw) ? stampRaw : null;
                this.userAvatarUrl = profileUser.avatar_url ||
                    `/api/users/${encodeURIComponent(userId)}/avatar${stamp ? `?v=${stamp}` : ''}`;
            } catch (error) {
                this.userAvatarUrl = null;
            }
        },
        handleUserAvatarError() {
            this.userAvatarUrl = null;
        },
        loadAccessContext() {
            const loads = [
                this.$store.dispatch('objectCards/fetchAccessSettings').catch(() => {}),
                this.$store.dispatch('getEmployeeDirectorySettings').catch(() => {})
            ];
            const canReadFuelLimits = this.$can('read', 'fuelLimits');
            if (!canReadFuelLimits) {
                loads.push(this.$store.dispatch('getObjectsInProcess', {silent: true}).catch(() => {}));
            }
            Promise.all(loads).then(() => {
                this.accessContextLoaded = true;
                this.showFallbackNoticeIfNeeded();
            });
        },
        refreshAttention() {
            if (!this.$store.getters.isAuthorized || this.hideNavigation) return;
            this.$store.dispatch('navigation_loadAttention').catch(() => {});
        },
        formatBadge(value) {
            const count = Number(value) || 0;
            return count > 99 ? '99+' : String(count);
        },
        isRouteUnder(path) {
            if (!path) return false;
            return this.$route.path === path || this.$route.path.indexOf(`${path}/`) === 0;
        },
        matchesWorkspace(workspace) {
            if (!workspace) return false;
            const path = routePath(workspace.path);
            if (!this.isRouteUnder(path)) return false;
            const expectedQuery = routeQuery(workspace.path);
            const keys = Object.keys(expectedQuery);
            return !keys.length || keys.every(key =>
                String(this.$route.query[key] || '') === String(expectedQuery[key])
            );
        },
        isWorkspaceActive(workspace) {
            return this.matchesWorkspace(workspace);
        },
        isNavigationLinkActive(link) {
            return this.matchesWorkspace(link);
        },
        nodeContainsActive(node) {
            if (!node) return false;
            if (node.kind === 'link') {
                return this.isNavigationLinkActive(node);
            }
            return Array.isArray(node.items) &&
                node.items.some(item => this.nodeContainsActive(item));
        },
        isNavigationGroupOpen(node) {
            if (!node) return false;
            if (this.closedNavigationGroups.indexOf(node.key) !== -1) return false;
            return this.nodeContainsActive(node) ||
                this.openNavigationGroups.indexOf(node.key) !== -1;
        },
        toggleNavigationGroup(node) {
            if (!node || !node.key) return;
            if (this.isNavigationGroupOpen(node)) {
                this.openNavigationGroups = this.openNavigationGroups.filter(key => (
                    key !== node.key
                ));
                if (this.closedNavigationGroups.indexOf(node.key) === -1) {
                    this.closedNavigationGroups = this.closedNavigationGroups.concat(node.key);
                }
            } else {
                if (this.isTopLevelNavigationSection(node)) {
                    const topLevelKeys = this.navigationSections.map(section => section.key);
                    this.openNavigationGroups = this.openNavigationGroups.filter(key => (
                        topLevelKeys.indexOf(key) === -1
                    ));
                    this.closedNavigationGroups = this.closedNavigationGroups
                        .filter(key => topLevelKeys.indexOf(key) === -1)
                        .concat(topLevelKeys.filter(key => key !== node.key));
                }
                this.openNavigationGroups = this.openNavigationGroups.concat(node.key);
                this.closedNavigationGroups = this.closedNavigationGroups.filter(key => (
                    key !== node.key
                ));
            }
            saveOpenNavigationGroups(this.user, this.openNavigationGroups);
        },
        isTopLevelNavigationSection(node) {
            return Boolean(
                node
                && this.navigationSections.some(section => section.key === node.key)
            );
        },
        syncActiveNavigationSection() {
            const sections = this.navigationSections;
            const activeSection = sections.find(section => this.nodeContainsActive(section));
            if (!activeSection) return;
            const topLevelKeys = sections.map(section => section.key);
            this.openNavigationGroups = this.openNavigationGroups.filter(key => (
                topLevelKeys.indexOf(key) === -1 || key === activeSection.key
            ));
            this.closedNavigationGroups = this.closedNavigationGroups.filter(key => (
                topLevelKeys.indexOf(key) === -1
            ));
            saveOpenNavigationGroups(this.user, this.openNavigationGroups);
        },
        isFlyoutSection(section) {
            return Boolean(
                section
                && this.sidebarFlyoutOpen
                && section.key === this.flyoutNavigationSectionKey
            );
        },
        shouldShowNavigationSection(section) {
            return this.compactSidebar
                ? this.isFlyoutSection(section)
                : this.isNavigationGroupOpen(section);
        },
        toggleSidebarFlyout(section) {
            if (!this.compactSidebar || !section || !section.key) return;
            this.closeAccountMenu();
            if (this.flyoutNavigationSectionKey === section.key) {
                this.closeSidebarFlyout(true);
                return;
            }
            this.flyoutNavigationSectionKey = section.key;
        },
        closeSidebarFlyout(restoreFocus = false) {
            const sectionKey = this.flyoutNavigationSectionKey;
            this.flyoutNavigationSectionKey = null;
            if (!restoreFocus || !sectionKey) return;
            this.$nextTick(() => {
                const buttons = this.$el
                    ? this.$el.querySelectorAll('[data-navigation-section-key]')
                    : [];
                const button = Array.prototype.find.call(buttons, item => (
                    item.getAttribute('data-navigation-section-key') === sectionKey
                ));
                if (button && typeof button.focus === 'function') button.focus();
            });
        },
        handleViewportResize() {
            this.viewportWidth = window.innerWidth;
            if (
                this.viewportWidth < 768
                || (this.viewportWidth >= 1200 && !this.effectiveDesktopSidebarCollapsed)
            ) {
                this.closeSidebarFlyout();
            }
        },
        syncTemporaryDesktopSidebarForRoute() {
            const shouldCollapse = Boolean(
                this.$route.meta &&
                this.$route.meta.collapseDesktopSidebarTemporarily === true
            );
            const routeKey = shouldCollapse
                ? String(this.$route.name || this.$route.path || 'route')
                : null;
            if (routeKey === this.temporaryDesktopSidebarRouteKey) return;
            this.temporaryDesktopSidebarRouteKey = routeKey;
            this.temporaryDesktopSidebarCollapsed = routeKey ? true : null;
            this.closeSidebarFlyout();
        },
        restoreOpenNavigationGroups() {
            this.openNavigationGroups = getOpenNavigationGroups(this.user);
            this.closedNavigationGroups = [];
            this.syncActiveNavigationSection();
        },
        restoreDesktopSidebar() {
            this.desktopSidebarCollapsed = getDesktopSidebarCollapsed(this.user);
        },
        setDesktopSidebarCollapsed(collapsed) {
            this.desktopSidebarCollapsed = collapsed === true;
            this.closeSidebarFlyout();
            saveDesktopSidebarCollapsed(this.user, this.desktopSidebarCollapsed);
        },
        handleDesktopSidebarCollapseRequest(event) {
            const detail = event && event.detail || {};
            if (detail.temporary && this.temporaryDesktopSidebarRouteKey) {
                this.temporaryDesktopSidebarCollapsed = detail.collapsed === true;
                this.closeSidebarFlyout();
                return;
            }
            this.setDesktopSidebarCollapsed(detail.collapsed === true);
        },
        toggleDesktopSidebar() {
            if (this.temporaryDesktopSidebarRouteKey) {
                this.temporaryDesktopSidebarCollapsed = !this.effectiveDesktopSidebarCollapsed;
                this.closeSidebarFlyout();
                return;
            }
            this.setDesktopSidebarCollapsed(!this.desktopSidebarCollapsed);
        },
        toggleAccountMenu() {
            this.closeSidebarFlyout();
            this.accountMenuOpen = !this.accountMenuOpen;
        },
        closeAccountMenu() {
            this.accountMenuOpen = false;
        },
        async refreshInstallAction() {
            const checkId = ++this.installationCheckId;
            try {
                if (
                    this.$store.getters.isTelegramWebApps
                    && isIOSInstallDevice()
                    && !hasTelegramInstallSuccessEligible()
                ) {
                    this.showInstallAction = false;
                    return;
                }
                const shouldShow = await shouldShowInstallPromotion();
                const handoff = shouldShow && this.$store.getters.isTelegramWebApps
                    ? await prepareTelegramInstallHandoff()
                    : null;
                if (checkId !== this.installationCheckId) {
                    return;
                }
                this.showInstallAction = Boolean(
                    shouldShow
                    && (
                        !this.$store.getters.isTelegramWebApps
                        || handoff
                    )
                );
            } catch (error) {
                if (checkId === this.installationCheckId) {
                    this.showInstallAction = false;
                }
            } finally {
                if (checkId === this.installationCheckId) {
                    // Профиль и install CTA появляются сразу в окончательной позиции.
                    this.installActionResolved = true;
                }
            }
        },
        async openInstallPage() {
            this.closeAccountMenu();
            if (this.$store.getters.isTelegramWebApps) {
                if (this.installActionBusy) return;
                this.installActionBusy = true;
                try {
                    const opened = await openInstallUrlExternally();
                    if (!opened && this.$bvToast) {
                        this.$bvToast.toast(
                            'Не удалось подготовить безопасный вход. Проверьте интернет и повторите.',
                            {
                                title: 'Не получилось открыть установку',
                                variant: 'warning',
                                solid: true
                            }
                        );
                    }
                } finally {
                    this.installActionBusy = false;
                }
                return;
            }
            markInstallPromotionSessionDismissed();
            this.$router.push('/install');
        },
        handleAccountMenuOutside(event) {
            const menu = this.$refs.accountMenu;
            if (
                this.accountMenuOpen &&
                menu &&
                event &&
                !menu.contains(event.target)
            ) {
                this.closeAccountMenu();
            }
        },
        handleAccountMenuKeydown(event) {
            if (!event || event.key !== 'Escape') return;
            if (this.accountMenuOpen) this.closeAccountMenu();
            if (this.sidebarFlyoutOpen) this.closeSidebarFlyout(true);
        },
        logoutFromAccountMenu() {
            this.closeAccountMenu();
            this.$emit('logout');
        },
        isPrimaryActive(key) {
            if (key === 'home') return this.$route.path === '/';
            if (key === 'tasks') return this.isRouteUnder('/tasks');
            if (key === 'workspace') return this.matchesWorkspace(this.effectiveWorkspace);
            if (key === 'requests') {
                return this.isRouteUnder('/requests') &&
                    (this.isTelegramWebApp || !this.matchesWorkspace(this.effectiveWorkspace));
            }
            if (key === 'more') {
                if (this.isRouteUnder('/more') || this.isRouteUnder('/profile') || this.isRouteUnder('/about')) {
                    return true;
                }
                if (
                    this.$route.path === '/' ||
                    this.isRouteUnder('/tasks') ||
                    this.isRouteUnder('/requests') ||
                    (!this.isTelegramWebApp && this.matchesWorkspace(this.effectiveWorkspace))
                ) {
                    return false;
                }
                return true;
            }
            return false;
        },
        showFallbackNoticeIfNeeded() {
            if (
                this.fallbackNoticeShown ||
                !this.navigation.loaded ||
                !this.accessContextLoaded
            ) return;
            const preferred = this.navigation.preferred_workspace_key;
            if (!preferred) return;
            const isAvailable = this.availableWorkspaces.some(item => item.key === preferred);
            if (isAvailable) return;
            this.fallbackNoticeShown = true;
            if (this.$bvToast) {
                this.$bvToast.toast(
                    'Выбранный раздел сейчас недоступен. Аргус открыл ближайший доступный вариант; изменить четвёртую вкладку можно в профиле.',
                    {
                        title: 'Моя вкладка',
                        variant: 'info',
                        solid: true,
                        autoHideDelay: 7000
                    }
                );
            }
        }
    }
};
</script>

<style scoped>
.modern-shell {
    min-height: 100vh;
    min-height: 100dvh;
    color: #1d2734;
    background: #f4f7fb;
    --shell-header-height: 64px;
    --shell-sidebar-width: 272px;
    --shell-blue: #176ff2;
    --shell-ink: #17202d;
}

.modern-shell__header {
    position: fixed;
    z-index: 1040;
    top: 0;
    right: 0;
    left: 0;
    display: grid;
    height: var(--shell-header-height);
    grid-template-columns: var(--shell-sidebar-width) minmax(0, 1fr) auto;
    align-items: center;
    border-bottom: 1px solid #dfe5ec;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 1px 10px rgba(31, 45, 61, 0.045);
    backdrop-filter: blur(14px);
}

.modern-shell__brand {
    display: flex;
    min-width: 0;
    height: 100%;
    align-items: center;
    padding: 0 20px;
    border-right: 1px solid #e4e9ef;
    color: var(--shell-ink);
    text-decoration: none;
}

.modern-shell__brand:hover,
.modern-shell__profile:hover,
.modern-shell__nav-link:hover,
.modern-shell__bottom-link:hover {
    text-decoration: none;
}

.modern-shell__brand-icon {
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    object-fit: cover;
    box-shadow: 0 4px 14px rgba(37, 84, 142, 0.16);
}

.modern-shell__brand-copy,
.modern-shell__profile-copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.modern-shell__brand-copy {
    margin-left: 12px;
}

.modern-shell__brand-copy strong {
    color: var(--shell-ink);
    font-size: 1.08rem;
    line-height: 1.15;
}

.modern-shell__brand-copy small,
.modern-shell__profile-copy small {
    overflow: hidden;
    color: #748092;
    font-size: 0.72rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-shell__page-title {
    min-width: 0;
    overflow: hidden;
    padding: 0 24px;
    color: #334052;
    font-size: 1.03rem;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-shell__profile {
    display: flex;
    min-width: 0;
    max-width: 280px;
    align-items: center;
    gap: 11px;
    padding: 8px 20px 8px 12px;
    border: 0;
    color: #303b4b;
    background: transparent;
    font: inherit;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
}

.modern-shell__header-actions {
    position: relative;
    display: flex;
    min-width: 0;
    align-items: center;
    padding-right: 14px;
}

.modern-shell__install-action {
    display: inline-flex;
    min-height: 38px;
    align-items: center;
    justify-content: center;
    margin-right: 7px;
    padding: 0 13px;
    border: 1px solid #b8d3fb;
    border-radius: 11px;
    color: #1559b7;
    background: #eef5ff;
    font: inherit;
    font-size: 0.78rem;
    font-weight: 800;
    white-space: nowrap;
    cursor: pointer;
}

.modern-shell__install-action i {
    margin-right: 7px;
}

.modern-shell__install-label--mobile {
    display: none;
}

.modern-shell__install-action:hover,
.modern-shell__install-action:focus {
    border-color: #83b4f8;
    color: #0f4d9f;
    background: #e1eeff;
    outline: none;
}

.modern-shell__install-action:focus-visible {
    box-shadow: 0 0 0 3px rgba(23, 111, 242, 0.18);
}

.modern-shell__install-action:disabled {
    cursor: wait;
    opacity: .62;
}

.modern-shell__profile:focus {
    outline: none;
}

.modern-shell__profile:focus-visible {
    border-radius: 12px;
    box-shadow: 0 0 0 3px rgba(23, 111, 242, 0.18);
}

.modern-shell__profile-chevron {
    color: #8994a4;
    font-size: 0.65rem;
    transition: transform 0.16s ease;
}

.modern-shell__profile-chevron--open {
    transform: rotate(180deg);
}

.modern-shell__account-menu {
    position: absolute;
    z-index: 1100;
    top: calc(100% + 6px);
    right: 14px;
    width: 250px;
    overflow: hidden;
    padding: 7px;
    border: 1px solid #dce3eb;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 16px 38px rgba(28, 43, 64, 0.18);
}

.modern-shell__account-summary {
    display: flex;
    min-width: 0;
    flex-direction: column;
    padding: 9px 10px 11px;
    border-bottom: 1px solid #edf0f4;
}

.modern-shell__account-summary strong,
.modern-shell__account-summary small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-shell__account-summary strong {
    color: #202936;
    font-size: 0.86rem;
}

.modern-shell__account-summary small {
    margin-top: 3px;
    color: #788496;
    font-size: 0.71rem;
}

.modern-shell__account-item {
    display: inline-flex;
    width: 100%;
    min-height: 42px;
    align-items: center;
    padding: 8px 10px;
    border: 0;
    border-radius: 10px;
    color: #394658;
    background: transparent;
    font: inherit;
    font-size: 0.82rem;
    font-weight: 700;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
}

.modern-shell__account-item:hover,
.modern-shell__account-item:focus {
    color: #105fcf;
    background: #eef5ff;
    outline: none;
    text-decoration: none;
}

.modern-shell__account-item i {
    flex: 0 0 24px;
    width: 24px;
    margin-right: 7px;
    color: #62738b;
    text-align: center;
}

.modern-shell__account-item--logout {
    margin-top: 4px;
    border-top: 1px solid #edf0f4;
    border-radius: 0 0 10px 10px;
    color: #a6242f;
}

.modern-shell__account-item--logout:hover,
.modern-shell__account-item--logout:focus,
.modern-shell__account-item--logout i {
    color: #a6242f;
}

.modern-shell__account-item--logout:hover,
.modern-shell__account-item--logout:focus {
    background: #fff1f2;
}

.modern-shell__profile-copy {
    align-items: flex-end;
}

.modern-shell__profile-copy strong {
    overflow: hidden;
    max-width: 190px;
    font-size: 0.84rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-shell__avatar {
    display: inline-flex;
    flex: 0 0 38px;
    width: 38px;
    height: 38px;
    align-items: center;
    justify-content: center;
    border: 1px solid #cbdcff;
    border-radius: 12px;
    color: #1556b4;
    background: #eaf2ff;
    font-size: 0.78rem;
    font-weight: 900;
    overflow: hidden;
}

.modern-shell__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.modern-shell__sidebar {
    position: fixed;
    z-index: 1020;
    top: var(--shell-header-height);
    bottom: 0;
    left: 0;
    display: flex;
    width: var(--shell-sidebar-width);
    flex-direction: column;
    border-right: 1px solid #dfe5ec;
    background: #fff;
    transition: width 180ms ease, box-shadow 180ms ease;
}

.modern-shell__sidebar-backdrop {
    position: fixed;
    z-index: 1015;
    top: var(--shell-header-height);
    right: 0;
    bottom: 0;
    left: 0;
    padding: 0;
    border: 0;
    background: rgba(22, 31, 43, 0.32);
    cursor: default;
    animation: modern-shell-backdrop-in 180ms ease both;
}

@keyframes modern-shell-backdrop-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

.modern-shell__sidebar-scroll {
    min-height: 0;
    flex: 1 1 auto;
    padding: 14px 12px 22px;
    overflow-x: hidden;
    overflow-y: auto;
}

.modern-shell__sidebar-collapse {
    display: none;
    min-height: 52px;
    flex: 0 0 auto;
    align-items: center;
    margin: 0 12px 12px;
    padding: 8px 10px;
    border: 0;
    border-top: 1px solid #e7edf4;
    border-radius: 10px;
    color: #1767cf;
    background: #fff;
    font: inherit;
    font-size: 0.84rem;
    font-weight: 800;
    text-align: left;
    cursor: pointer;
}

.modern-shell__sidebar-collapse:hover,
.modern-shell__sidebar-collapse:focus {
    color: #0d55b4;
    background: #edf4ff;
    outline: none;
}

.modern-shell__sidebar-collapse:focus-visible {
    box-shadow: 0 0 0 3px rgba(23, 111, 242, 0.18);
}

.modern-shell__sidebar-collapse i {
    flex: 0 0 32px;
    width: 32px;
    font-size: 1.12rem;
    text-align: center;
}

.modern-shell__sidebar-collapse span {
    margin-left: 10px;
    white-space: nowrap;
}

.modern-shell__nav-group + .modern-shell__nav-group {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid #edf0f4;
}

.modern-shell__nav-heading {
    margin: 0 10px 8px;
    color: #8a95a5;
    font-size: 0.69rem;
    font-weight: 800;
    letter-spacing: 0.075em;
    text-transform: uppercase;
}

.modern-shell__nav-section + .modern-shell__nav-section {
    margin-top: 5px;
}

.modern-shell__nav-toggle,
.modern-shell__nav-subtoggle {
    display: flex;
    width: 100%;
    align-items: center;
    border: 0;
    color: #4a5667;
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;
}

.modern-shell__nav-toggle {
    min-height: 46px;
    padding: 7px 10px;
    border-radius: 12px;
}

.modern-shell__nav-toggle:hover,
.modern-shell__nav-toggle:focus,
.modern-shell__nav-subtoggle:hover,
.modern-shell__nav-subtoggle:focus {
    color: #145fc8;
    background: #f1f6ff;
    outline: none;
}

.modern-shell__nav-toggle--active,
.modern-shell__nav-subtoggle--active {
    color: #105bc4;
    font-weight: 800;
}

.modern-shell__nav-toggle--active .modern-shell__nav-icon {
    color: #155fc7;
    background: #eaf2ff;
}

.modern-shell__nav-toggle-chevron {
    flex: 0 0 auto;
    margin-left: auto;
    color: #98a3b2;
    font-size: 0.66rem;
}

.modern-shell__nav-children {
    margin: 2px 0 8px 16px;
    padding-left: 8px;
    border-left: 1px solid #e4e9ef;
}

.modern-shell__nav-subgroup + .modern-shell__nav-subgroup {
    margin-top: 2px;
}

.modern-shell__nav-subtoggle {
    min-height: 38px;
    padding: 6px 9px;
    border-radius: 10px;
    font-size: 0.82rem;
    font-weight: 700;
}

.modern-shell__nav-subtoggle > i:first-child {
    flex: 0 0 22px;
    width: 22px;
    color: #697587;
    text-align: center;
}

.modern-shell__nav-subtoggle > span {
    min-width: 0;
    margin-left: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-shell__nav-grandchildren {
    margin-left: 10px;
}

.modern-shell__nav-link {
    position: relative;
    display: flex;
    min-height: 46px;
    align-items: center;
    margin: 3px 0;
    padding: 7px 10px;
    border-radius: 12px;
    color: #4a5667;
    text-decoration: none;
    transition: color 0.14s ease, background-color 0.14s ease;
}

.modern-shell__nav-link:hover {
    color: #145fc8;
    background: #f1f6ff;
}

.modern-shell__nav-link--active {
    color: #105bc4;
    background: #eaf2ff;
    font-weight: 800;
}

.modern-shell__nav-icon {
    display: inline-flex;
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    color: #637084;
    background: #f2f4f7;
}

.modern-shell__nav-link--active .modern-shell__nav-icon {
    color: #fff;
    background: var(--shell-blue);
    box-shadow: 0 4px 11px rgba(23, 111, 242, 0.25);
}

.modern-shell__nav-label {
    min-width: 0;
    margin-left: 10px;
    overflow: hidden;
    font-size: 0.84rem;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-shell__nav-link--section,
.modern-shell__nav-link--nested {
    min-height: 40px;
    padding-top: 5px;
    padding-bottom: 5px;
}

.modern-shell__nav-link--section .modern-shell__nav-icon,
.modern-shell__nav-link--nested .modern-shell__nav-icon {
    flex-basis: 28px;
    width: 28px;
    height: 28px;
    font-size: 0.78rem;
}

.modern-shell__nav-link--nested .modern-shell__nav-label {
    font-size: 0.8rem;
}

.modern-shell__nav-badge {
    display: inline-flex;
    min-width: 23px;
    height: 23px;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0 6px;
    border-radius: 999px;
    color: #fff;
    background: #dc3545;
    font-size: 0.68rem;
    font-weight: 900;
}

.modern-shell__favorite {
    margin-left: auto;
    color: #f0a600;
    font-size: 0.72rem;
}

.modern-shell__content {
    box-sizing: border-box;
    min-height: 100vh;
    min-height: 100dvh;
    padding-top: var(--shell-header-height);
    padding-right: 24px;
    padding-left: calc(var(--shell-sidebar-width) + 24px);
    transition: padding-left 180ms ease;
}

.modern-shell__content >>> .requests-page {
    --requests-page-offset: var(--shell-header-height);
}

.modern-shell__bottom-nav {
    position: fixed;
    z-index: 1030;
    right: 0;
    bottom: 0;
    left: 0;
    display: none;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    min-height: 68px;
    padding: 5px 4px calc(5px + env(safe-area-inset-bottom));
    border-top: 1px solid #dbe2ea;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 -5px 20px rgba(32, 51, 75, 0.08);
    backdrop-filter: blur(16px);
}

.modern-shell__bottom-link {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 3px;
    border-radius: 11px;
    color: #758194;
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 1.05;
    text-decoration: none;
}

.modern-shell__bottom-link--active {
    color: #105fcf;
    background: #edf4ff;
}

.modern-shell__bottom-link > span:last-child {
    display: block;
    width: 100%;
    overflow: hidden;
    padding: 0 2px;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-shell__bottom-icon {
    position: relative;
    display: inline-flex;
    width: 31px;
    height: 29px;
    align-items: center;
    justify-content: center;
    font-size: 1.12rem;
}

.modern-shell__bottom-badge {
    position: absolute;
    top: -5px;
    right: -7px;
    display: inline-flex;
    min-width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    border: 2px solid #fff;
    border-radius: 999px;
    color: #fff;
    background: #dc3545;
    font-size: 0.55rem;
    font-weight: 900;
}

.modern-shell--immersive .modern-shell__content {
    padding: 12px 24px 24px;
}

@media (min-width: 1200px) {
    .modern-shell__sidebar-collapse {
        display: flex;
    }

    .modern-shell--sidebar-collapsed {
        --shell-sidebar-width: 76px;
    }

    .modern-shell--sidebar-collapsed .modern-shell__brand {
        justify-content: center;
        padding: 0;
    }

    .modern-shell--sidebar-collapsed .modern-shell__brand-icon {
        width: 40px;
        height: 40px;
        flex-basis: 40px;
    }

    .modern-shell--sidebar-collapsed .modern-shell__brand-copy,
    .modern-shell--sidebar-collapsed .modern-shell__nav-label,
    .modern-shell--sidebar-collapsed .modern-shell__nav-heading,
    .modern-shell--sidebar-collapsed .modern-shell__favorite,
    .modern-shell--sidebar-collapsed .modern-shell__sidebar-collapse span {
        display: none;
    }

    .modern-shell--sidebar-collapsed .modern-shell__sidebar-scroll {
        padding-right: 10px;
        padding-left: 10px;
    }

    .modern-shell--sidebar-collapsed .modern-shell__nav-link {
        justify-content: center;
        padding-right: 0;
        padding-left: 0;
    }

    .modern-shell--sidebar-collapsed .modern-shell__catalog {
        display: none;
    }

    .modern-shell--sidebar-collapsed .modern-shell__nav-icon {
        flex-basis: 38px;
        width: 38px;
        height: 38px;
    }

    .modern-shell--sidebar-collapsed .modern-shell__nav-badge {
        position: absolute;
        top: 2px;
        right: -2px;
        min-width: 20px;
        height: 20px;
        padding: 0 4px;
        border: 2px solid #fff;
        font-size: 0.61rem;
    }

    .modern-shell--sidebar-collapsed .modern-shell__sidebar-collapse {
        justify-content: center;
        margin-right: 10px;
        margin-left: 10px;
        padding-right: 0;
        padding-left: 0;
    }

    .modern-shell--sidebar-collapsed .modern-shell__sidebar-collapse i {
        flex-basis: 38px;
        width: 38px;
    }
}

@media (min-width: 1200px) and (max-width: 1500px) {
    .modern-shell__content {
        padding-right: 12px;
        padding-left: calc(var(--shell-sidebar-width) + 12px);
    }
}

@media (min-width: 768px) and (max-width: 1199.98px) {
    .modern-shell {
        --shell-sidebar-width: 76px;
    }

    .modern-shell__header {
        grid-template-columns: var(--shell-sidebar-width) minmax(0, 1fr) auto;
    }

    .modern-shell__brand {
        justify-content: center;
        padding: 0;
    }

    .modern-shell__brand-icon {
        width: 40px;
        height: 40px;
        flex-basis: 40px;
    }

    .modern-shell__brand-copy,
    .modern-shell__nav-label,
    .modern-shell__nav-heading,
    .modern-shell__favorite {
        display: none;
    }

    .modern-shell__sidebar-scroll {
        padding-right: 10px;
        padding-left: 10px;
    }

    .modern-shell__nav-link {
        justify-content: center;
        padding-right: 0;
        padding-left: 0;
    }

    .modern-shell__nav-icon {
        flex-basis: 38px;
        width: 38px;
        height: 38px;
    }

    .modern-shell__nav-badge {
        position: absolute;
        top: 2px;
        right: -2px;
        min-width: 20px;
        height: 20px;
        padding: 0 4px;
        border: 2px solid #fff;
        font-size: 0.61rem;
    }
}

@media (min-width: 768px) {
    .modern-shell--sidebar-compact .modern-shell__catalog {
        display: block;
    }

    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__nav-label,
    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__nav-heading,
    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__favorite,
    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__sidebar-collapse span {
        display: none;
    }

    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__nav-children {
        display: none !important;
    }

    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__nav-link,
    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__nav-toggle {
        justify-content: center;
        padding-right: 0;
        padding-left: 0;
    }

    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__nav-icon {
        flex-basis: 38px;
        width: 38px;
        height: 38px;
    }

    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__nav-toggle-chevron {
        display: none;
    }

    .modern-shell--sidebar-compact:not(.modern-shell--sidebar-flyout-open)
    .modern-shell__nav-badge {
        position: absolute;
        top: 2px;
        right: -2px;
        min-width: 20px;
        height: 20px;
        padding: 0 4px;
        border: 2px solid #fff;
        font-size: 0.61rem;
    }

    .modern-shell--sidebar-flyout-open .modern-shell__sidebar {
        z-index: 1025;
        width: 272px;
        box-shadow: 14px 0 32px rgba(25, 38, 56, 0.18);
    }

    .modern-shell--sidebar-flyout-open .modern-shell__sidebar-scroll {
        padding: 14px 12px 22px;
    }

    .modern-shell--sidebar-flyout-open .modern-shell__nav-link,
    .modern-shell--sidebar-flyout-open .modern-shell__nav-toggle {
        justify-content: flex-start;
        padding-right: 10px;
        padding-left: 10px;
    }

    .modern-shell--sidebar-flyout-open .modern-shell__nav-icon {
        flex-basis: 32px;
        width: 32px;
        height: 32px;
    }

    .modern-shell--sidebar-flyout-open .modern-shell__nav-label,
    .modern-shell--sidebar-flyout-open .modern-shell__sidebar-collapse span {
        display: inline;
    }

    .modern-shell--sidebar-flyout-open .modern-shell__nav-badge {
        position: static;
        min-width: 23px;
        height: 23px;
        margin-left: auto;
        padding: 0 6px;
        border: 0;
        font-size: 0.68rem;
    }

    .modern-shell--sidebar-flyout-open .modern-shell__sidebar-collapse {
        justify-content: flex-start;
        margin-right: 12px;
        margin-left: 12px;
        padding-right: 10px;
        padding-left: 10px;
    }

    .modern-shell--sidebar-flyout-open .modern-shell__sidebar-collapse i {
        flex-basis: 32px;
        width: 32px;
    }

    .modern-shell__nav-toggle--selected {
        color: #105bc4;
        background: #eaf2ff;
        font-weight: 800;
    }

    .modern-shell__nav-toggle--selected .modern-shell__nav-icon {
        color: #fff;
        background: var(--shell-blue);
        box-shadow: 0 4px 11px rgba(23, 111, 242, 0.25);
    }
}

@media (prefers-reduced-motion: reduce) {
    .modern-shell__sidebar,
    .modern-shell__content {
        transition: none;
    }

    .modern-shell__sidebar-backdrop {
        animation: none;
    }
}

@media (max-width: 767.98px) {
    .modern-shell {
        --shell-header-height: 56px;
        --shell-sidebar-width: 0px;
    }

    .modern-shell__header {
        grid-template-columns: minmax(0, 1fr) auto;
        padding: 0 12px;
    }

    .modern-shell__brand {
        border-right: 0;
        padding: 0;
    }

    .modern-shell__brand-icon {
        width: 38px;
        height: 38px;
        flex-basis: 38px;
        border-radius: 11px;
    }

    .modern-shell__brand-copy {
        margin-left: 9px;
    }

    .modern-shell__brand-copy small {
        max-width: 155px;
    }

    .modern-shell__page-title,
    .modern-shell__profile-copy,
    .modern-shell__sidebar {
        display: none;
    }

    .modern-shell__profile {
        padding: 0;
    }

    .modern-shell__header-actions {
        padding-right: 0;
    }

    .modern-shell__install-action {
        width: auto;
        max-width: 142px;
        min-height: 36px;
        margin-right: 8px;
        padding: 0 9px;
        font-size: .68rem;
        line-height: 1.1;
        white-space: normal;
    }

    .modern-shell__install-action i {
        margin-right: 6px;
    }

    .modern-shell__install-label--desktop {
        display: none;
    }

    .modern-shell__install-label--mobile {
        display: inline;
    }

    .modern-shell__avatar {
        width: 36px;
        height: 36px;
        flex-basis: 36px;
        border-radius: 11px;
    }

    .modern-shell__account-menu {
        top: calc(100% + 7px);
        right: 0;
        width: min(250px, calc(100vw - 24px));
    }

    .modern-shell__content {
        padding-top: var(--shell-header-height);
        padding-right: 8px;
        padding-bottom: calc(72px + env(safe-area-inset-bottom));
        padding-left: 8px;
    }

    .modern-shell__content >>> .requests-page {
        --requests-page-offset: calc(
            var(--shell-header-height) + 72px + env(safe-area-inset-bottom)
        );
    }

    .modern-shell__bottom-nav {
        display: grid;
    }

    .modern-shell--bottom-nav-hidden .modern-shell__content,
    .modern-shell--telegram.modern-shell--bottom-nav-hidden:not(.modern-shell--immersive) .modern-shell__content {
        padding-bottom: calc(8px + env(safe-area-inset-bottom));
    }

    .modern-shell--immersive .modern-shell__content {
        padding: 4px 8px 16px;
    }
}

.modern-shell--telegram:not(.modern-shell--immersive) .modern-shell__content {
    padding: 4px 8px calc(72px + env(safe-area-inset-bottom));
}

.modern-shell--telegram:not(.modern-shell--immersive) .modern-shell__content >>> .requests-page {
    --requests-page-offset: calc(72px + env(safe-area-inset-bottom));
}

.modern-shell__bottom-nav--telegram {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

@media (prefers-reduced-motion: reduce) {
    .modern-shell__nav-link {
        transition: none;
    }
}
</style>
